import { apiClient } from './client';
import { uploadToPresignedUrl, finalizeUpload, safeUploadContentType } from './files';

/**
 * Workspace API client (P3-FILE-FE) — backend `apps/chat-server/src/modules/workspace/workspace.routes.ts` 와 1:1.
 *
 * 폴더는 file 모델의 type='folder' 재사용 (backend 정합).
 */

export interface NodePublic {
  id: string;
  type: string;       // 'folder' | 'chat-attachment' | 'workspace-file' | etc.
  name: string;
  parentId: string | null;
  uploadedBy: string;
  mimeType: string | null;
  sizeBytes: number;
  verified: boolean;
  createdAt: string;
  // 2026-08-12 additive — 스토리지 스코프 + 문서 제목(검색 매칭 대상).
  // 서버가 항상 내려주지만, 캐시된 옛 응답을 대비해 optional 로 둔다.
  ownerScope?: string | null;
  ownerTeamId?: string | null;
  docTitle?: string | null;
  // 2026-08-14 additive — 드라이브 목록의 "수정일" 열, 폴더 색상, 시스템 폴더 표식.
  updatedAt?: string;
  /**
   * 하위 **폴더**가 있는가 (2026-08-19 additive) — 좌측 트리의 펼침 화살표 판정용.
   * 폴더 노드에만 실린다. 구버전 서버 응답에는 없으므로 소비처가 기본값을 정해야 한다.
   */
  hasChildFolder?: boolean;
  color?: string | null;
  /** 'company-root' 면 회사 공유 폴더. 이름이 바뀌어도 이 값으로 식별한다. */
  systemKey?: string | null;
  // 2026-08-14 additive — 목록 배지. 참조/공유가 없으면 아예 안 온다.
  /** 이 파일이 어디에 쓰이나 — 대표 종류 하나 + 총 참조 수. */
  usage?: UsageSummary;
  /** 공유 대상 요약 — '내가 공유함' 목록의 대상 열. */
  shareSummary?: {
    targetCount: number; firstTargetName: string | null; hasCompany: boolean;
    /** 상위 폴더에서 물려받은 공유가 섞였다 (2026-10-27). 옛 서버는 안 준다. */
    inherited?: boolean;
  };
}

/**
 * 참조 종류 — 서버 `workspace-usage.ts` 의 UsageKind 와 같은 어휘.
 *
 * ⚠ 서버가 출처를 새로 켜면(회의록·결재 등) 이 목록에 **없는 값이 내려온다.**
 *   그래도 화면이 깨지면 안 된다 — 렌더는 `smartMetaOf()`(WorkspaceSmartFolders.tsx)를
 *   거치고, 그 함수가 모르는 값에 기본 아이콘·이름을 붙인다. 여기 이름을 더하는 것은
 *   "제대로 된 아이콘·번역을 붙이는" 일이지 "동작하게 만드는" 일이 아니다.
 */
export type UsageKind = 'chat' | 'task' | 'mail' | 'calendar' | 'board'
  // 아직 서버가 세지 않는 후보 — 붙는 순간 이름·아이콘이 이미 준비돼 있게 미리 등록한다.
  | 'minutes' | 'approval' | 'reservation' | 'memo'
  // 위 어느 것도 아닌 미래 값. 유니온을 열어 두어야 새 출처가 타입 오류 없이 흐른다.
  | (string & {});
/** 스마트 폴더 소스 — 참조 종류 + 'none'(어디에도 안 쓰임). */
export type UsageSource = UsageKind | 'none';

export interface UsageSummary { kind: UsageKind; count: number }

export interface UsageRef {
  kind: UsageKind;
  id: string;
  /** 방 이름 · 메일 제목 · 일정 제목 · 게시글 제목 · 업무 제목. */
  label: string;
  /** 업무의 프로젝트명 등. */
  sublabel?: string;
}

export interface UsageReport {
  refs: UsageRef[];
  total: number;
  /** 영구삭제를 DB 가 막는가(프로젝트 업무 첨부). */
  blocksHardDelete: boolean;
}

/** 이 파일이 어디에 쓰이고 있나. 삭제 전 경고·세부정보 패널이 쓴다. */
export async function getFileUsage(id: string): Promise<UsageReport> {
  const res = await apiClient.get<UsageReport>(`/workspace/files/${id}/usage`);
  return res.data;
}

/** 개인 스토리지 사용량 — 목록 응답에 함께 실려 온다(2026-08-12). */
export interface StorageUsage {
  scopeType: 'personal' | 'team' | 'company';
  usedBytes: number;
  /** -1 이면 무제한. */
  limitBytes: number;
  pct: number;
  unlimited: boolean;
  warn: boolean;
}

export interface ListFolderResponse {
  parent: string | null;
  children: NodePublic[];
  // 2026-08-12 additive. 옛 응답을 대비해 전부 optional.
  nextCursor?: string | null;
  searching?: boolean;
  usage?: StorageUsage;
  /**
   * 2026-08-14 — 출처별 스마트 폴더. **내 파일 루트에서만** 온다(0건 소스는 빠진다).
   * 폴더 안이나 검색 중에는 뜻이 없어 서버가 보내지 않는다.
   */
  smartFolders?: Array<{ source: UsageSource; count: number }>;
  /**
   * 2026-08-21 — 폴더 구획. 페이지와 무관하게 **전량** 온다.
   *
   * 예전에는 폴더가 파일과 같은 페이지 흐름에 섞여 있어서, 첨부가 많은 계정에서는
   * 첫 페이지에 폴더가 한 건도 안 실렸다(좌측 트리가 통째로 비었다). 이제 서버가
   * 폴더를 따로 뽑아 준다 — 화면은 이것을 목록 맨 위에 고정해 그린다.
   *
   * 폴더 경계가 없는 범위(홈·공유됨·검색)에서는 빈 배열이고, 그때는 예전처럼 폴더가
   * `children` 안에 섞여 온다. 옛 서버는 이 필드를 아예 안 보낸다(undefined).
   */
  folders?: NodePublic[];
  /** 2026-08-21 — 총 **파일** 수(폴더 제외). 번호 페이저가 페이지 수를 낸다. */
  total?: number;
  /** 폴더가 구획 상한(500)을 넘어 잘렸다. */
  folderTruncated?: boolean;
}

/** 목록 조회 옵션 (2026-08-12). 전부 선택 — 미지정 시 기존 동작. */
export interface ListFolderOptions {
  /** 파일명·문서 제목 검색어. 지정하면 서버가 **전체 스토리지를 재귀 검색**한다. */
  q?: string;
  /** 유형 필터 — all | word | excel | ppt | pdf | markup | txt | image | etc. */
  kind?: string;
  limit?: number;
  /**
   * 다음 페이지 토큰. **불투명 값으로 다뤄야 한다** — 정렬을 지정하면 서버가 이 값을
   * id 커서가 아니라 offset 으로 해석한다(id 커서는 id 순서일 때만 안정적이라서).
   * 그대로 되돌려주기만 하면 되고, 같은 요청에 sort 도 함께 보내야 해석이 어긋나지 않는다.
   */
  cursor?: string;
  // 2026-08-14 additive — 드라이브 정렬·범위.
  /**
   * 정렬 축 — name | type | updated | size | shared.
   * `shared`(공유일)는 **공유됨 범위 전용**이다 — 다른 범위에서 보내면 서버가 수정일로 본다.
   */
  sort?: 'name' | 'type' | 'updated' | 'size' | 'shared';
  order?: 'asc' | 'desc';
  /**
   * 목록 범위.
   *   folder(기본) · home(최근 수정) · shared(나에게 공유됨) ·
   *   shared-by-me(내가 공유함) · source(출처별 스마트 폴더 — `source` 와 함께) ·
   *   company(회사공유 최상위 — 회사 폴더 직속 + 관리자가 등록한 전사 공유)
   */
  scope?: 'folder' | 'home' | 'shared' | 'shared-by-me' | 'source' | 'company';
  /** scope=source 일 때의 출처. 'none' 은 어디에도 쓰이지 않는 파일. */
  source?: UsageSource;
  /**
   * 2026-08-21 — 좌측 트리 전용. 폴더만 받고 배지·공유·스마트폴더·용량을 안 받는다.
   * 옛 서버는 이 파라미터를 모르므로 400 을 준다 → `listFolderChildren` 이 빼고 재시도한다.
   */
  foldersOnly?: boolean;
}

export interface CreateFolderInput {
  name: string;
  parentId?: string | null;
  ownerScope?: 'personal' | 'team' | 'company';
  ownerTeamId?: string | null;
}

export interface PatchFolderInput {
  name?: string;
  parentId?: string | null;
}

/**
 * parent 'root' 또는 folder id.
 *
 * 두 번째 인자는 2026-08-12 additive — 기존 호출부(인자 1개)는 그대로 동작한다.
 * ⚠ 서버 스키마가 `additionalProperties:false` 라, 여기서 보내는 파라미터 이름이
 *   workspace.routes.ts 의 listQuerySchema 와 정확히 일치해야 한다(아니면 400).
 */
export async function listFolderChildren(
  parent: string = 'root',
  opts: ListFolderOptions = {},
): Promise<ListFolderResponse> {
  const params: Record<string, string | number | boolean> = { parent };
  if (opts.q) params.q = opts.q;
  // 'all' 은 필터 없음과 같으므로 굳이 보내지 않는다(쿼리 문자열 소음 감소).
  if (opts.kind && opts.kind !== 'all') params.kind = opts.kind;
  if (opts.limit) params.limit = opts.limit;
  if (opts.cursor) params.cursor = opts.cursor;
  if (opts.sort) params.sort = opts.sort;
  if (opts.order) params.order = opts.order;
  // 'folder' 는 기본값이라 굳이 보내지 않는다(쿼리 문자열 소음 감소).
  if (opts.scope && opts.scope !== 'folder') params.scope = opts.scope;
  if (opts.source) params.source = opts.source;
  if (opts.foldersOnly) params.foldersOnly = true;

  /*
   * 배포 순서 결합 제거 (2026-08-21).
   *
   * 서버 스키마가 `additionalProperties:false` 라 **옛 서버는 새 파라미터에 400** 을 준다.
   * 그러면 프론트가 먼저 나갔을 때 좌측 트리가 통째로 빈다. 400 이면 그 파라미터만 빼고
   * 한 번 다시 부른다 — 결과는 예전과 같고(폴더가 children 에 섞여 온다), 배포 순서를
   * 지킬 필요가 없어진다. 메일 presign 때 쓴 것과 같은 수법이다.
   */
  const fetch = (p: Record<string, string | number | boolean>) =>
    apiClient.get<ListFolderResponse>('/workspace/folders', { params: p });
  let res;
  try {
    res = await fetch(params);
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status !== 400 || !opts.foldersOnly) throw e;
    const { foldersOnly: _drop, ...rest } = params;
    res = await fetch(rest);
  }
  // 방어 가드(additive) — children 이 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return { ...res.data, children: Array.isArray(res.data?.children) ? res.data.children : [] };
}

/**
 * 목록 응답에서 폴더를 꺼낸다 (2026-08-21).
 *
 * 서버가 `folders` 를 주면 그것이 진실이다 — **페이지와 무관한 전량**이라, 첨부가 아무리
 * 많아도 폴더가 빠지지 않는다. 옛 서버(또는 폴더 경계가 없는 범위)면 예전처럼 `children`
 * 안에서 골라낸다.
 */
export function foldersOf(res: ListFolderResponse): NodePublic[] {
  if (Array.isArray(res.folders)) return res.folders;
  return res.children.filter((c) => c.type === 'folder');
}

export async function createFolder(input: CreateFolderInput): Promise<NodePublic> {
  const res = await apiClient.post<{ folder: NodePublic }>('/workspace/folders', input);
  return res.data.folder;
}

export async function getFolder(id: string): Promise<NodePublic> {
  const res = await apiClient.get<{ folder: NodePublic }>(`/workspace/folders/${id}`);
  return res.data.folder;
}

export async function patchFolder(id: string, input: PatchFolderInput): Promise<NodePublic> {
  const res = await apiClient.patch<{ folder: NodePublic }>(`/workspace/folders/${id}`, input);
  return res.data.folder;
}

export async function deleteFolder(id: string): Promise<void> {
  await apiClient.delete(`/workspace/folders/${id}`);
}

// ─── 드라이브 (2026-08-14) ──────────────────────────────────────────────
//
// 파일 조작·휴지통·회사 폴더. 기존 export 는 하나도 건드리지 않는다(회귀 0).

/** 휴지통 항목 — 목록 노드에 삭제 시각이 붙는다. */
export interface TrashNode extends NodePublic {
  deletedAt: string | null;
}

export async function listTrash(limit?: number): Promise<TrashNode[]> {
  const res = await apiClient.get<{ items: TrashNode[] }>('/workspace/trash', {
    params: limit ? { limit } : undefined,
  });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export async function restoreFromTrash(
  id: string,
): Promise<{ file: NodePublic | null; movedToRoot: boolean }> {
  const res = await apiClient.post<{ file: NodePublic | null; movedToRoot: boolean }>(
    `/workspace/trash/${id}/restore`,
  );
  return res.data;
}

export async function purgeFromTrash(id: string): Promise<void> {
  await apiClient.delete(`/workspace/trash/${id}`);
}

export async function emptyTrash(): Promise<{ purged: number; failed: number; hasMore: boolean }> {
  const res = await apiClient.delete<{ purged: number; failed: number; hasMore: boolean }>(
    '/workspace/trash',
  );
  return res.data;
}

export interface CompanyFolderResponse {
  folder: NodePublic;
  /** 업로드·삭제 버튼을 보일지. **권한 경계가 아니다** — 실제 차단은 서버가 한다. */
  canManage: boolean;
}

export async function getCompanyFolder(): Promise<CompanyFolderResponse> {
  const res = await apiClient.get<CompanyFolderResponse>('/workspace/company-folder');
  return res.data;
}

/** 파일 이름 변경 · 이동 · 폴더 색상. 폴더에도 같은 라우트를 쓴다. */
export async function patchFile(
  id: string,
  input: { name?: string; parentId?: string | null; color?: string | null },
): Promise<NodePublic> {
  const res = await apiClient.patch<{ file: NodePublic }>(`/workspace/files/${id}`, input);
  return res.data.file;
}

/** 휴지통으로 보낸다(soft delete). 폴더면 하위까지 함께. */
export async function deleteFile(id: string): Promise<void> {
  await apiClient.delete(`/workspace/files/${id}`);
}

/**
 * 복제 · 다음으로 복사.
 *
 * `parentId` 를 생략하면 원본과 같은 폴더에 "(사본)" 이름으로 만든다(= 복제).
 */
export async function copyFile(
  id: string,
  input: { parentId?: string | null; name?: string } = {},
): Promise<NodePublic> {
  const res = await apiClient.post<{ file: NodePublic }>(`/workspace/files/${id}/copy`, input);
  return res.data.file;
}

/** 접근 수준 — 서버 workspace-access.ts 와 같은 어휘. */
export type AccessLevel = 'none' | 'view' | 'edit' | 'owner';

export interface FileDetail {
  file: NodePublic;
  access: AccessLevel;
  /** 루트 → 부모 순서의 경로. 최상위면 빈 배열. */
  path: Array<{ id: string; name: string }>;
  owner: { id: string; name: string } | null;
  inCompanyFolder: boolean;
}

export async function getFileDetail(id: string): Promise<FileDetail> {
  const res = await apiClient.get<FileDetail>(`/workspace/files/${id}/detail`);
  return res.data;
}

export interface ActivityItem {
  id: string;
  eventType: string;
  actorId: string | null;
  actorName: string | null;
  createdAt: string;
}

export async function getFileActivity(id: string, limit?: number): Promise<ActivityItem[]> {
  const res = await apiClient.get<{ items: ActivityItem[] }>(`/workspace/files/${id}/activity`, {
    params: limit ? { limit } : undefined,
  });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

// ─── 용량 관리 (2026-08-14 2차) ─────────────────────────────────────────
//
// 서버 `workspace-storage.routes.ts` 와 1:1. 좌측 하단 '용량관리' 가 쓴다.

/** 폴더 하나(또는 루트)의 사용량. `id: null` = 폴더에 들어 있지 않은 파일 묶음. */
export interface StorageBucket {
  id: string | null;
  /** 폴더 이름. 루트 묶음이거나 이름을 못 찾으면 빈 문자열 — 화면이 문구를 정한다. */
  name: string;
  sizeBytes: number;
  fileCount: number;
}

/** 집계 범위 (2026-08-14 2차) — 개인 · 팀 · 회사. 팀·회사는 **보기 전용**이다. */
export type StorageScope = 'personal' | 'team' | 'company';

export interface StorageBreakdown {
  usage: StorageUsage;
  buckets: StorageBucket[];
  /** 파일이 상한을 넘어 일부만 집계했는가. 화면이 그 사실을 알린다. */
  truncated: boolean;
  // 2026-08-14 (2차) additive — 옛 응답을 대비해 전부 optional.
  scope?: StorageScope;
  teamId?: string | null;
  /** 내가 들여다볼 수 있는 팀(관리자는 전체). 선택 상자의 재료. */
  teams?: Array<{ id: string; name: string }>;
  /** 비우기를 보일지 — 팀·회사에는 남의 파일이 섞여 있어 서버가 false 를 준다. */
  canEmpty?: boolean;
}

export async function getStorageBreakdown(
  opts: { scope?: StorageScope; teamId?: string | null } = {},
): Promise<StorageBreakdown> {
  const params: Record<string, string> = {};
  // 'personal' 은 기본값이라 굳이 보내지 않는다(쿼리 문자열 소음 감소).
  if (opts.scope && opts.scope !== 'personal') params.scope = opts.scope;
  if (opts.teamId) params.teamId = opts.teamId;
  const res = await apiClient.get<StorageBreakdown>('/workspace/storage/breakdown', { params });
  return {
    ...res.data,
    buckets: Array.isArray(res.data?.buckets) ? res.data.buckets : [],
  };
}

/**
 * 폴더를 비운다 — 안의 내 파일을 **휴지통으로** 보낸다(되돌릴 수 있다).
 * `folderId: null` 이면 폴더에 들어 있지 않은 내 파일들.
 */
export async function emptyStorageFolder(folderId: string | null): Promise<{ deleted: number }> {
  const res = await apiClient.post<{ deleted: number }>('/workspace/storage/empty', { folderId });
  return res.data;
}

export type ShareTargetType = 'user' | 'team' | 'company';
export type SharePermission = 'VIEW' | 'EDIT';

export interface ShareEntry {
  id: string;
  targetType: ShareTargetType;
  targetId: string;
  /** 대상이 지워졌으면 null — 그래도 행은 보여 준다(해제할 수 있어야 하므로). */
  targetName: string | null;
  permission: SharePermission;
  expiresAt: string | null;
  createdAt: string;
  /** 폴더 공유가 하위 폴더까지 닿는가 (2026-10-27 · 마이그 241). 옛 서버는 안 준다 — 없으면 true 로 읽는다. */
  includeSubfolders?: boolean;
}

export interface SharesResponse {
  owner: { id: string; name: string } | null;
  /** 공유를 걸고 풀 수 있는가. 공유받은 사람은 재공유할 수 없다. */
  canManage: boolean;
  shares: ShareEntry[];
  /**
   * 상위 폴더에서 물려받은 공유 (2026-10-27) — 읽기 전용. 해제는 `fromId` 폴더에서 한다.
   * 옛 서버는 안 준다 — 없으면 빈 목록으로 읽는다.
   */
  inherited?: Array<ShareEntry & { fromId: string; fromName: string }>;
}

export async function listShares(fileId: string): Promise<SharesResponse> {
  const res = await apiClient.get<SharesResponse>(`/workspace/files/${fileId}/shares`);
  return res.data;
}

export async function addShare(
  fileId: string,
  input: {
    targetType: ShareTargetType;
    /** company 면 생략(서버가 0 으로 고정). user/team 이면 필수. */
    targetId?: string;
    permission?: SharePermission;
    expiresAt?: string | null;
    /** 폴더일 때만 보낸다 — 옛 서버는 모르는 칸을 400 으로 막는다. */
    includeSubfolders?: boolean;
  },
): Promise<ShareEntry> {
  const res = await apiClient.post<{ share: ShareEntry }>(
    `/workspace/files/${fileId}/shares`, input,
  );
  return res.data.share;
}

export async function removeShare(
  fileId: string, targetType: ShareTargetType, targetId: string,
): Promise<void> {
  await apiClient.delete(`/workspace/files/${fileId}/shares/${targetType}/${targetId}`);
}

/**
 * 파일 type → emoji (시안 line 2513 fileEmojis).
 *
 * @deprecated 2026-08-12 — UI 는 이모지 대신 SVG 아이콘을 쓴다(components/WorkspaceFileIcon).
 *   내부 판정은 lib/file-kind.ts 의 exact MIME 표로 옮겼다. 예전 `includes('document')`
 *   휴리스틱은 pptx 를 word 로 오분류했다. 이 함수는 남아 있는 호출부 호환용이며
 *   신규 코드에서는 쓰지 말 것.
 */
export function fileEmoji(node: Pick<NodePublic, 'type' | 'mimeType'>): string {
  if (node.type === 'folder') return '📁';
  const mime = node.mimeType?.toLowerCase() ?? '';
  if (mime.startsWith('image/')) return '🖼';
  if (mime.startsWith('video/')) return '🎬';
  if (mime.startsWith('audio/')) return '🎵';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '📽';
  if (mime.includes('zip') || mime.includes('archive')) return '🗜';
  return '📎';
}

// 2026-04-29 attachment-ssot-refactor: formatBytes 3중 SSOT 통합 — lib/attachments.ts 단일 위치.
// workspace 사용처 (FileGrid / FileListView) 호환성 유지를 위해 re-export.
// GB 정밀도 `.toFixed(1) → .toFixed(2)` intentional drift correction.
export { formatBytes } from '../lib/attachments';

// ─── 업로드 (2026-08-12) ────────────────────────────────────────────────
//
// 지금까지 워크스페이스에는 업로드 경로가 없었다(채팅 첨부만 루트에 흘러들었다).
// 3단계 흐름은 채팅과 동일하고 presign 엔드포인트만 다르다 — uploadToPresignedUrl /
// finalizeUpload 는 api/files.ts 의 기존 헬퍼를 그대로 재사용한다(검증 로직 복제 금지).

export interface WorkspacePresignResponse {
  fileId: string;
  fileKey: string;
  uploadUrl: string;
  expiresAt: string;
  ownerScope: string;
  ownerTeamId: string | null;
}

/** 서버가 413 으로 돌려주는 쿼터 초과 본문. */
export interface QuotaExceededBody {
  code: 'QUOTA_EXCEEDED';
  message: string;
  scopeType: 'personal' | 'team' | 'company';
  usedBytes: number;
  limitBytes: number;
}

export async function presignWorkspaceUpload(input: {
  filename: string;
  contentType: string;
  sizeBytes: number;
  parentId?: string | null;
}): Promise<WorkspacePresignResponse> {
  const { data } = await apiClient.post<WorkspacePresignResponse>(
    '/workspace/files/presigned-upload', input,
  );
  return data;
}

/**
 * 파일 하나를 워크스페이스에 올린다 — presign → MinIO PUT → finalize.
 *
 * ★ content-type 판정은 채팅과 **같은 함수**(`safeUploadContentType`)를 쓴다 (2026-08-14).
 *   종전엔 여기서 `file.type || 'application/octet-stream'` 을 직접 만들어, 브라우저가 빈
 *   MIME 을 주는 형식(.hwp/.hwpx/.7z/.rar…)이 전부 octet-stream 으로 declare 되어 서버
 *   화이트리스트에 **415** 로 막혔다. 채팅에는 확장자→정규 MIME 표가 이미 있었는데 이 경로만
 *   그것을 안 타고 있었다 — 두 경로가 다른 규칙을 갖는 상태 자체가 결함이다.
 *   (HTML 안전화·압축·한글 문서 declare 가 한꺼번에 따라온다.)
 */
export async function uploadWorkspaceFile(
  file: File,
  parentId: string | null,
  onProgress?: (percent: number) => void,
): Promise<{ fileId: string }> {
  const contentType = safeUploadContentType(file);
  const presigned = await presignWorkspaceUpload({
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    parentId,
  });
  await uploadToPresignedUrl(presigned.uploadUrl, file, contentType, {
    onProgress: onProgress ? (ev) => onProgress(ev.percent) : undefined,
  });
  await finalizeUpload(presigned.fileId);
  return { fileId: presigned.fileId };
}

// ─── 텍스트 덮어쓰기 (2026-08-14) ───────────────────────────────────────
//
// 오피스 편집기는 `@workchat/office` 안의 shim 이 같은 두 엔드포인트를 직접 부른다.
// 텍스트 편집기는 그 무거운 번들을 받을 이유가 없어(textarea 하나다) 여기에 얇은
// 헬퍼를 둔다. **엔드포인트·순서는 같다** — presign → PUT → finalize.
//
// ⚠ Content-Type 은 반드시 presign 이 준 값을 그대로 보낸다. 서명이 그 헤더까지 포함해
//   계산되므로 다른 값을 보내면 MinIO 가 403 을 준다.

interface ReplacePresignResponse {
  fileId: string;
  fileKey: string;
  uploadUrl: string;
  expiresAt: string;
  contentType: string;
  previousSizeBytes: number;
}

/** 텍스트 파일 내용을 통째로 덮어쓴다. 성공하면 서버가 관측한 새 크기. */
export async function replaceWorkspaceFileText(
  fileId: string,
  text: string,
): Promise<{ sizeBytes: number }> {
  const bytes = new TextEncoder().encode(text);
  const { data: presigned } = await apiClient.post<ReplacePresignResponse>(
    `/workspace/files/${fileId}/presigned-replace`, { sizeBytes: bytes.byteLength },
  );
  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presigned.contentType },
    body: new Blob([bytes], { type: presigned.contentType }),
  });
  if (!put.ok) throw new Error(`업로드 실패 (HTTP ${put.status})`);
  const { data } = await apiClient.post<{ sizeBytes: number }>(
    `/workspace/files/${fileId}/finalize-replace`,
  );
  return data;
}
